import {InfiniteBrandSelector} from '@rdcomponents/brand-select/infinite-brand-selector';
import {CompanySelectionModeEnum} from '@rdcomponents/stories/yandex-story-form/slides-content-steps/mode-selection-step/yandex-story-mode-selection-step-type';
import {Text} from '@rdcomponents/text/text';
import {Radio, RadioChangeEvent} from 'antd';
import {useCallback, useEffect, useState} from 'react';

import {AdditionalInfo} from '../../../../../layout/additional-info/additional-info';
import {useLocale} from '../../../../../provider/locale/locale-hook';
import {UseCompaniesSelectorHookType} from '../../../../../service/feature-companies/feature-companies-type';
import {
    YandexStoryFormFieldsEnum,
    YandexStoryFormType,
} from '../../../../../service/yandex-stories/yandex-stories-type';
import {FormInstance} from '../../../../../typings/antd';
import {YANDEX_SELECT_COMPANIES_STEP_WIDTH} from '../../yandex-story-form-helper';
import {YandexStoryFormStep} from '../step/yandex-story-form-step';

import * as styles from './yandex-story-mode-selection-step.scss';

type PropsType = {
    formInstance: FormInstance<YandexStoryFormType>;
    onStepFinished: () => void;
    onCancel: VoidFunction;
    onStepActivated: (width: number) => void;
    selectedBrandId: number | null;
    onBrandIdChange: (brandId: number | null) => void;
    companiesSelector: UseCompaniesSelectorHookType;
};

export function YandexStoryModeSelectionStep(props: PropsType): JSX.Element {
    const {
        formInstance,
        onStepFinished,
        onCancel,
        onStepActivated,
        selectedBrandId,
        onBrandIdChange,
        companiesSelector,
    } = props;

    const {getLocalizedString} = useLocale();

    const [mode, setMode] = useState<CompanySelectionModeEnum>(
        formInstance.getFieldValue(YandexStoryFormFieldsEnum.AutoAddCompanies)
            ? CompanySelectionModeEnum.AllBrandCompanies
            : CompanySelectionModeEnum.Manual
    );

    const [selectedBrand, setSelectedBrand] = useState<number | null>(selectedBrandId);

    useEffect(() => {
        setSelectedBrand(selectedBrandId);
    }, [selectedBrandId]);

    useEffect(() => {
        onStepActivated(YANDEX_SELECT_COMPANIES_STEP_WIDTH);
    }, [onStepActivated]);

    useEffect(() => {
        const currentAutoAddCompanies = formInstance.getFieldValue(YandexStoryFormFieldsEnum.AutoAddCompanies);

        if (mode === CompanySelectionModeEnum.AllBrandCompanies && !currentAutoAddCompanies) {
            formInstance.setFieldsValue({[YandexStoryFormFieldsEnum.AutoAddCompanies]: true});
        } else if (mode === CompanySelectionModeEnum.Manual && currentAutoAddCompanies) {
            formInstance.setFieldsValue({[YandexStoryFormFieldsEnum.AutoAddCompanies]: false});
            setSelectedBrand(null);
            onBrandIdChange(null);
        }
    }, [formInstance, mode, onBrandIdChange]);

    const handleModeChange = useCallback(
        (event: RadioChangeEvent) => {
            const newMode = event.target.value as CompanySelectionModeEnum;

            setMode(newMode);

            if (newMode === CompanySelectionModeEnum.AllBrandCompanies) {
                formInstance.setFieldsValue({[YandexStoryFormFieldsEnum.AutoAddCompanies]: true});
            } else {
                formInstance.setFieldsValue({[YandexStoryFormFieldsEnum.AutoAddCompanies]: false});
                setSelectedBrand(null);
                onBrandIdChange(null);
            }
        },
        [formInstance, onBrandIdChange]
    );

    const handleBrandChange = useCallback(
        (brandId: number) => {
            setSelectedBrand(brandId);
            onBrandIdChange(brandId);
        },
        [onBrandIdChange]
    );

    const handleNext = useCallback(async () => {
        if (mode === CompanySelectionModeEnum.AllBrandCompanies && selectedBrandId) {
            await companiesSelector.deselectAll();
            companiesSelector.selectFiltered({
                brand_ids: [selectedBrandId],
            });
        }

        onStepFinished();
    }, [companiesSelector, mode, onStepFinished, selectedBrandId]);

    const isNextDisabled = mode === CompanySelectionModeEnum.AllBrandCompanies && !selectedBrand;

    return (
        <YandexStoryFormStep
            leftFooterButtonConfig={{
                langKeyType: 'BUTTON__CANCEL',
                onClick: onCancel,
            }}
            rightFooterButtonConfig={{
                langKeyType: 'BUTTON__NEXT',
                onClick: handleNext,
                disabled: isNextDisabled,
            }}
        >
            <div className={styles.YandexStoryModeSelectionStep}>
                <Radio.Group
                    className={styles.YandexStoryModeSelectionStep_modeSelector}
                    onChange={handleModeChange}
                    value={mode}
                >
                    <Radio value={CompanySelectionModeEnum.Manual}>
                        <Text large stringKey="YANDEX_STORY__MODE_SELECTION__MANUAL" />
                    </Radio>
                    <Radio value={CompanySelectionModeEnum.AllBrandCompanies}>
                        <div className={styles.YandexStoryModeSelectionStep_brandSelectOption}>
                            <Text large stringKey="YANDEX_STORY__MODE_SELECTION__AUTO" />
                            <AdditionalInfo>
                                <Text stringKey="YANDEX_STORY__MODE_SELECTION__AUTO__TOOLTIP" />
                            </AdditionalInfo>
                        </div>
                    </Radio>
                </Radio.Group>

                {mode === CompanySelectionModeEnum.AllBrandCompanies && (
                    <InfiniteBrandSelector
                        className={styles.YandexStoryModeSelectionStep_brandSelector}
                        onChange={handleBrandChange}
                        placeholder={getLocalizedString('YANDEX_STORY__MODE_SELECTION__SELECT_BRAND')}
                        value={selectedBrand}
                    />
                )}
            </div>
        </YandexStoryFormStep>
    );
}
